import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import PawPrint from "@/components/PawPrint";
import SeverityBadge from "@/components/SeverityBadge";
import BottomNav from "@/components/BottomNav";

const EMOJI_MAP = { Chat: "\ud83d\udc31", Chien: "\ud83d\udc36", Lapin: "\ud83d\udc30", Oiseau: "\ud83d\udc26", Rongeur: "\ud83d\udc39", Reptile: "\ud83e\udd8e", Autre: "\ud83d\udc3e" };
const COLORS_MAP = { Chat: "#FFB7C5", Chien: "#FBBF59", Lapin: "#A8D8EA", Oiseau: "#C3E8FF", Rongeur: "#D4C5F0", Reptile: "#B5E8C0", Autre: "#E0E0E0" };
const BG_MAP = { Chat: "linear-gradient(135deg,#FFE4EC,#FFB7C5)", Chien: "linear-gradient(135deg,#FFF4DC,#FBBF59)", Lapin: "linear-gradient(135deg,#DCF0F8,#A8D8EA)" };

function enrichAnimal(a) {
  return {
    ...a,
    emoji: EMOJI_MAP[a.species] || "\ud83d\udc3e",
    color: COLORS_MAP[a.species] || "#E0E0E0",
    bg: BG_MAP[a.species] || `linear-gradient(135deg,${COLORS_MAP[a.species] || "#E0E0E0"}44,${COLORS_MAP[a.species] || "#E0E0E0"})`,
  };
}

/* Niveaux de gravit\u00e9 extraits de la r\u00e9ponse IA */
const SEVERITY = {
  green:  { bg: "#E8F8F0", border: "#6FCF97", dot: "#27AE60", label: "\ud83d\udfe2 Pas urgent",    text: "Situation sans urgence imm\u00e9diate." },
  yellow: { bg: "#FFF8E8", border: "#F9C74F", dot: "#F0A500", label: "\ud83d\udfe1 \u00c0 surveiller",  text: "Consultez un v\u00e9t\u00e9rinaire prochainement." },
  red:    { bg: "#FFF0F0", border: "#FF7070", dot: "#E53535", label: "\ud83d\udd34 Urgent",         text: "Consultez un v\u00e9t\u00e9rinaire rapidement !" },
};

function detectSeverity(text) {
  const match = text.match(/NIVEAU:(green|yellow|red)/i);
  return match ? match[1] : "green";
}

function cleanText(text) {
  return text.replace(/NIVEAU:(green|yellow|red)/gi, "").trim();
}

/* -- Renderer Markdown -- */
function renderMarkdown(text) {
  const lines = text.split("\n");
  const elements = [];
  let listBuffer = [];
  let key = 0;

  const flushList = () => {
    if (listBuffer.length === 0) return;
    elements.push(
      <ul key={key++} className="my-1.5 ml-1 pl-[18px] flex flex-col gap-[3px]">
        {listBuffer.map((item, i) => (
          <li key={i} className="font-body text-sm leading-relaxed font-semibold text-inherit">
            {inlineStyles(item)}
          </li>
        ))}
      </ul>
    );
    listBuffer = [];
  };

  const inlineStyles = (str) => {
    const parts = [];
    const regex = /(\*\*(.+?)\*\*|_(.+?)_|`(.+?)`)/g;
    let last = 0, match;
    while ((match = regex.exec(str)) !== null) {
      if (match.index > last) parts.push(str.slice(last, match.index));
      if (match[2]) parts.push(<strong key={match.index} className="font-black">{match[2]}</strong>);
      else if (match[3]) parts.push(<em key={match.index} className="italic opacity-85">{match[3]}</em>);
      else if (match[4]) parts.push(<code key={match.index} className="bg-black/[0.08] rounded px-[5px] py-px text-[13px] font-mono">{match[4]}</code>);
      last = match.index + match[0].length;
    }
    if (last < str.length) parts.push(str.slice(last));
    return parts.length === 1 && typeof parts[0] === "string" ? parts[0] : parts;
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) { flushList(); elements.push(<div key={key++} className="h-1.5" />); continue; }

    if (trimmed.startsWith("## ")) {
      flushList();
      elements.push(
        <div key={key++} style={{ fontFamily: "'Fredoka One', cursive", fontSize: 15, color: "#FF8C42", marginTop: 10, marginBottom: 2 }}>
          {trimmed.slice(3)}
        </div>
      );
    } else if (trimmed.startsWith("# ")) {
      flushList();
      elements.push(
        <div key={key++} style={{ fontFamily: "'Fredoka One', cursive", fontSize: 17, color: "#FF7055", marginTop: 10, marginBottom: 4 }}>
          {trimmed.slice(2)}
        </div>
      );
    } else if (trimmed.startsWith("- ") || trimmed.startsWith("\u2022 ")) {
      listBuffer.push(trimmed.slice(2));
    } else {
      flushList();
      elements.push(
        <p key={key++} className="my-0.5 leading-relaxed font-body text-sm font-semibold">
          {inlineStyles(trimmed)}
        </p>
      );
    }
  }
  flushList();
  return elements;
}

/* -- Bulle message -- */
function MessageBubble({ msg, isLast }) {
  const isUser = msg.role === "user";
  const sev = msg.severity ? SEVERITY[msg.severity] : null;

  return (
    <div className={`flex items-end gap-2 mb-4 ${isUser ? "flex-row-reverse" : "flex-row"} ${isLast ? "animate-slide-up" : ""}`}>
      {!isUser && (
        <div className="w-[34px] h-[34px] rounded-full shrink-0 bg-pawcare-gradient flex items-center justify-center shadow-[0_3px_10px_rgba(255,112,85,0.35)]">
          <PawPrint size={18} color="white" />
        </div>
      )}

      <div className={`max-w-[75%] flex flex-col gap-1.5 ${isUser ? "items-end" : "items-start"}`}>
        {msg.image && (
          <img
            src={msg.image}
            alt="photo animale"
            className="max-w-[200px] rounded-2xl shadow-[0_4px_14px_rgba(0,0,0,0.12)] border-2 border-white/80"
          />
        )}

        {msg.text && (
          <div
            className={`
              px-4 py-3 font-body text-sm leading-relaxed font-semibold
              ${isUser
                ? "bg-pawcare-gradient text-white rounded-[20px_20px_6px_20px] shadow-[0_4px_16px_rgba(255,112,85,0.35)]"
                : "bg-white text-[#3A3A3A] rounded-[20px_20px_20px_6px] shadow-[0_4px_14px_rgba(0,0,0,0.07)] border-[1.5px] border-black/5"
              }
            `}
          >
            {isUser ? msg.text : renderMarkdown(msg.text)}
            {msg.streaming && (
              <span className="inline-flex gap-[3px] ml-1.5 align-middle">
                {[0, 1, 2].map(i => (
                  <span
                    key={i}
                    className="w-[5px] h-[5px] rounded-full bg-pawcare-amber inline-block"
                    style={{ animation: `bounce 1s ease ${i * 0.2}s infinite` }}
                  />
                ))}
              </span>
            )}
          </div>
        )}

        {sev && !msg.streaming && (
          <SeverityBadge severity={msg.severity} config={sev} />
        )}
      </div>
    </div>
  );
}

/* -- Suggestions rapides -- */
function QuickReplies({ animal, onSelect }) {
  const suggestions = [
    `${animal.emoji} ${animal.name} ne mange plus depuis hier`,
    `${animal.emoji} ${animal.name} vomit ce matin`,
    `${animal.emoji} ${animal.name} se gratte beaucoup`,
    `${animal.emoji} ${animal.name} boite l\u00e9g\u00e8rement`,
  ];
  return (
    <div className="flex gap-2 overflow-x-auto px-4 pb-3 scrollbar-none">
      {suggestions.map((s, i) => (
        <button
          key={i}
          onClick={() => onSelect(s)}
          className="
            bg-white border-[1.5px] border-pawcare-amber/30 rounded-[20px]
            px-3.5 py-2 font-body text-[12.5px] font-bold text-pawcare-orange
            cursor-pointer whitespace-nowrap shrink-0
            shadow-[0_2px_8px_rgba(255,140,66,0.1)]
            transition-all duration-200
            hover:bg-gradient-to-br hover:from-[#FFF4DC] hover:to-[#FFE4EC]
            hover:border-pawcare-amber
          "
        >
          {s}
        </button>
      ))}
    </div>
  );
}

/* -- S\u00e9lecteur animal -- */
function AnimalPicker({ animals, selected, onSelect }) {
  return (
    <div className="flex gap-2 px-4 py-2.5 overflow-x-auto scrollbar-none">
      {animals.map(a => (
        <button
          key={a.id}
          onClick={() => onSelect(a)}
          className="
            flex items-center gap-1.5 rounded-[20px] py-1.5 pl-2 pr-3.5
            cursor-pointer font-body font-extrabold text-[13px] text-[#3A3A3A]
            transition-all duration-[250ms] ease-[cubic-bezier(0.34,1.56,0.64,1)]
            shrink-0
          "
          style={{
            background: selected?.id === a.id ? a.bg : "rgba(255,255,255,0.8)",
            border: selected?.id === a.id ? `2px solid ${a.color}` : "2px solid rgba(255,255,255,0.6)",
            boxShadow: selected?.id === a.id ? `0 4px 14px ${a.color}55` : "0 2px 6px rgba(0,0,0,0.06)",
            transform: selected?.id === a.id ? "scale(1.05)" : "scale(1)",
          }}
        >
          <span className="text-lg">{a.emoji}</span>
          {a.name}
        </button>
      ))}
    </div>
  );
}

/* -- APP -- */
export default function ChatPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { API, token } = useAuth();
  const [animals, setAnimals] = useState([]);
  const [animal, setAnimal] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [showQuick, setShowQuick] = useState(true);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const textareaRef = useRef(null);

  // Fetch animals from backend
  useEffect(() => {
    API.get("/api/animals").then(res => {
      const enriched = res.data.map(enrichAnimal);
      setAnimals(enriched);
      if (enriched.length > 0 && !animal) {
        // Pre-select animal from navigation state if provided
        const preselectedId = location.state?.animalId;
        const target = preselectedId
          ? enriched.find(a => a.id === preselectedId) || enriched[0]
          : enriched[0];
        setAnimal(target);
        setMessages([{
          role: "assistant",
          text: `Bonjour ! \ud83d\udc3e Je suis votre assistant v\u00e9t\u00e9rinaire PawCare.\n\nComment puis-je vous aider avec ${target.name} aujourd'hui ? D\u00e9crivez-moi ses sympt\u00f4mes ou posez-moi vos questions \u2014 je suis l\u00e0 24h/24 \ud83d\udc95`,
          severity: null,
        }]);
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API]);

  useEffect(() => {
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    });
  }, [messages]);

  /* Reset conversation quand on change d'animal */
  const handleAnimalChange = (a) => {
    setAnimal(a);
    setMessages([{
      role: "assistant",
      text: `Bonjour ! \ud83d\udc3e Je suis pr\u00eat \u00e0 vous aider avec ${a.name} ${a.emoji}.\n\nQue se passe-t-il ? D\u00e9crivez-moi ce que vous observez \ud83d\udc95`,
      severity: null,
    }]);
    setShowQuick(true);
    setPendingImage(null);
  };

  /* Upload photo */
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert("Image trop volumineuse (max 5 Mo)");
      return;
    }
    if (!file.type.startsWith("image/")) {
      alert("Veuillez s\u00e9lectionner une image");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setPendingImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  /* Envoi message */
  const sendMessage = async (text = input) => {
    if ((!text.trim() && !pendingImage) || loading || !animal) return;

    const userMsg = {
      role: "user",
      text: text.trim() || (pendingImage ? "Voici une photo de mon animal." : ""),
      image: pendingImage,
    };

    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    setPendingImage(null);
    setShowQuick(false);
    setLoading(true);

    /* Placeholder streaming */
    const assistantPlaceholder = { role: "assistant", text: "", streaming: true, severity: null };
    setMessages(prev => [...prev, assistantPlaceholder]);

    try {
      /* Construction historique pour l'API */
      const apiMessages = newMessages.map(m => {
        if (m.role === "user" && m.image) {
          return {
            role: "user",
            content: [
              { type: "image", source: { type: "base64", media_type: "image/jpeg", data: m.image.split(",")[1] } },
              { type: "text", text: m.text || "Voici une photo de mon animal, qu'en pensez-vous ?" },
            ]
          };
        }
        return { role: m.role, content: m.text };
      });

      const res = await fetch((process.env.REACT_APP_BACKEND_URL || "") + "/api/consultations/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          animal_id: animal.id,
          messages: apiMessages,
        }),
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let full = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        const lines = chunk.split("\n").filter(l => l.startsWith("data: "));
        for (const line of lines) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === "content_block_delta" && data.delta?.text) {
              full += data.delta.text;
              const cleaned = cleanText(full);
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: "assistant", text: cleaned, streaming: true, severity: null };
                return updated;
              });
            } else if (data.type === "error") {
              throw new Error(data.message || "Erreur du serveur");
            }
          } catch (parseErr) {
            if (parseErr.message && !parseErr.message.includes("JSON")) throw parseErr;
          }
        }
      }

      const severity = detectSeverity(full);
      const cleaned = cleanText(full);
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: "assistant", text: cleaned, streaming: false, severity };
        return updated;
      });

    } catch (err) {
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: "assistant",
          text: "D\u00e9sol\u00e9, une erreur s'est produite. Veuillez r\u00e9essayer. \ud83d\ude4f",
          streaming: false, severity: "yellow",
        };
        return updated;
      });
    }

    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!animal) {
    return (
      <div className="min-h-screen bg-app-gradient flex flex-col items-center justify-center px-8 text-center">
        <div className="text-5xl mb-4">{"\ud83d\udc3e"}</div>
        <div className="font-display text-xl text-app-text mb-2">Aucun animal enregistré</div>
        <p className="text-sm text-app-muted font-semibold">
          Ajoutez votre premier animal dans l'onglet "Mes animaux" pour démarrer une consultation.
        </p>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes bounce {
          0%,80%,100% { transform:translateY(0); }
          40%         { transform:translateY(-5px); }
        }
      `}</style>

      <div className="h-screen h-[-webkit-fill-available] flex flex-col bg-pawcare-gradient font-body overflow-hidden">

        {/* -- HEADER -- */}
        <div className="bg-pawcare-gradient shrink-0 px-5 shadow-[0_4px_24px_rgba(255,112,85,0.3)]"
          style={{ background: "linear-gradient(135deg, #FFB347 0%, #FF7055 50%, #FF5C8D 100%)", paddingTop: "max(16px, env(safe-area-inset-top, 16px))" }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <PawPrint size={28} color="rgba(255,255,255,0.92)" />
              <div>
                <div className="font-display text-xl text-white leading-none">
                  PawCare
                </div>
                <div className="text-[10px] text-white/75 font-semibold">
                  Consultation vétérinaire IA
                </div>
              </div>
            </div>

            <button
              onClick={() => navigate("/urgences")}
              className="
              bg-white/[0.22] border-[1.5px] border-white/40 rounded-xl
              px-3.5 py-[7px] font-body font-black text-xs text-white
              cursor-pointer backdrop-blur-[8px]
              flex items-center gap-[5px] animate-fade-in
            ">
              {"\ud83d\udea8"} Urgences
            </button>
          </div>

          {/* S\u00e9lecteur animal */}
          <AnimalPicker animals={animals} selected={animal} onSelect={handleAnimalChange} />
        </div>

        {/* -- MESSAGES -- */}
        <div className="flex-1 overflow-y-auto px-4 pt-5 pb-2 flex flex-col">
          <div className="mt-auto" />
          {messages.map((msg, i) => (
            <MessageBubble key={i} msg={msg} isLast={i === messages.length - 1} />
          ))}

          {showQuick && !loading && (
            <div className="animate-fade-in" style={{ animationDelay: "0.3s", animationFillMode: "both" }}>
              <div className="font-body text-[11.5px] text-[#888] font-bold text-center mb-2.5">
                Suggestions rapides
              </div>
              <QuickReplies animal={animal} onSelect={(s) => { setInput(s); setShowQuick(false); }} />
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* -- ZONE SAISIE -- */}
        <div className="border-t border-pawcare-amber/15 bg-white/[0.94] backdrop-blur-[18px] px-3.5 pt-2.5 pb-1 shrink-0 mb-[72px]">
          {pendingImage && (
            <div className="flex items-center gap-2.5 mb-2.5 p-2 px-3 bg-[#FFF8F0] rounded-[14px] border-[1.5px] border-pawcare-amber/30 animate-slide-up">
              <img src={pendingImage} alt="preview" className="w-12 h-12 rounded-[10px] object-cover" />
              <div className="flex-1">
                <div className="font-extrabold text-xs text-pawcare-orange">Photo prête à envoyer {"\ud83d\udcf7"}</div>
                <div className="text-[11px] text-[#BBB] font-semibold">Ajoutez un message si besoin</div>
              </div>
              <button
                onClick={() => setPendingImage(null)}
                className="w-6 h-6 rounded-full bg-[#FFE0E0] border-none cursor-pointer text-xs text-[#E55]"
              >
                {"\u2715"}
              </button>
            </div>
          )}

          <div className="flex items-end gap-2 bg-[#F8F8F8] rounded-[22px] py-2 pl-4 pr-2 border-[1.5px] border-pawcare-amber/25 shadow-[0_2px_12px_rgba(0,0,0,0.06)]">
            <button
              onClick={() => fileRef.current?.click()}
              className="w-9 h-9 rounded-xl border-none cursor-pointer text-lg flex items-center justify-center shrink-0 transition-all duration-200"
              style={{
                background: pendingImage
                  ? "linear-gradient(135deg,#FFB347,#FF7055)"
                  : "rgba(255,179,71,0.12)",
              }}
            >
              {"\ud83d\udcf7"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />

            <textarea
              ref={textareaRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`D\u00e9crivez les sympt\u00f4mes de ${animal.name}\u2026`}
              rows={1}
              className="flex-1 bg-transparent border-none font-body text-sm font-semibold text-[#3A3A3A] leading-normal max-h-[100px] overflow-y-auto pt-1.5 resize-none outline-none"
              onInput={e => {
                e.target.style.height = "auto";
                e.target.style.height = Math.min(e.target.scrollHeight, 100) + "px";
              }}
            />

            <button
              onClick={() => sendMessage()}
              disabled={loading || (!input.trim() && !pendingImage)}
              className="w-10 h-10 rounded-[14px] border-none flex items-center justify-center text-lg shrink-0 transition-all duration-[250ms] ease-[cubic-bezier(0.34,1.56,0.64,1)]"
              style={{
                background: (loading || (!input.trim() && !pendingImage))
                  ? "rgba(200,200,200,0.4)"
                  : "linear-gradient(135deg, #FFB347, #FF7055, #FF5C8D)",
                cursor: loading ? "wait" : "pointer",
                boxShadow: (!loading && (input.trim() || pendingImage))
                  ? "0 4px 14px rgba(255,112,85,0.4)" : "none",
                transform: (!loading && (input.trim() || pendingImage)) ? "scale(1.05)" : "scale(1)",
              }}
            >
              {loading ? "\u23f3" : "\u27a4"}
            </button>
          </div>

          <p className="text-[10px] text-[#999] text-center leading-relaxed font-semibold mt-1.5 mb-0.5">
            PawCare ne remplace pas un vétérinaire. En cas d'urgence, appelez votre clinique. {"\ud83e\ude7a"}
          </p>
        </div>
      </div>

      {/* -- BOTTOM NAV (hors du flex container) -- */}
      <BottomNav />
    </>
  );
}
